import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Card,
  CardContent,
  Chip,
  Avatar,
  LinearProgress,
  CircularProgress,
  IconButton,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Close,
  Gavel,
  AccessAlarm,
  TrendingUp,
  Person,
  AttachMoney,
  Timer,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AuctionService } from '../../services/auctionService';
import BidPlacementHistoryPopup from './BidPlacementHistoryPopup';
 import { supabase } from '../../lib/supabase';

interface BidPlacementPopupProps {
  open: boolean;
  onClose: () => void;
  product: {
    id: string;
    name: string;
    image: string;
    price: number;
    auction?: {
      id: string;
      end_time: string;
      current_bid: number;
      bid_increment: number;
      status: string;
      reserve_price?: number;
      bid_count: number;
      highest_bidder?: {
        name: string;
        avatar?: string;
      };
    };
  };
  onPlaceBid: (bidAmount: number) => Promise<void>;
}

const BidPlacementPopup: React.FC<BidPlacementPopupProps> = ({
  open,
  onClose,
  product,
  onPlaceBid
}) => {
  const [bidAmount, setBidAmount] = useState<number>(0);
  const [isPlacingBid, setIsPlacingBid] = useState(false);
  const [bidSuccess, setBidSuccess] = useState(false);
  const [outbidOpen, setOutbidOpen] = useState(false);
  const [outbidHighest, setOutbidHighest] = useState<number | null>(null);
  const [isUserHighest, setIsUserHighest] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState('');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [endedOpen, setEndedOpen] = useState(false);

   const [refreshingLatest, setRefreshingLatest] = useState(false);
   const [latestAuction, setLatestAuction] = useState<{
     id: string;
     end_time: string;
     current_bid: number;
     bid_increment: number;
     status: string;
     reserve_price?: number;
     bid_count: number;
   } | null>(null);

   const auction = latestAuction || product.auction;
  const currentBid = auction?.current_bid || product.price;
  const bidIncrement = auction?.bid_increment || 5.00;
  const reservePrice = auction?.reserve_price || 0;
  const bidCount = auction?.bid_count || 0;

   // Fetch newest auction data (highest bid) on open
   useEffect(() => {
     let isMounted = true;
    const refreshLatest = async () => {
       if (!open || !product.auction?.id) return;
       try {
         setRefreshingLatest(true);
         setError(''); // Clear errors when popup opens
        const bids = await AuctionService.getAuctionBids(product.auction.id);
        const highestBid = bids && bids.length > 0 ? bids[0] : null;
        const highest = highestBid ? highestBid.bid_amount : (product.auction.current_bid ?? product.price);
         if (!isMounted) return;
         setLatestAuction({
           id: product.auction.id,
           end_time: product.auction.end_time,
           current_bid: highest,
           bid_increment: product.auction.bid_increment ?? 1.0,
           status: product.auction.status,
           reserve_price: product.auction.reserve_price,
          bid_count: bids?.length ?? (product.auction.bid_count || 0),
         });
        // Load current user and set status chip using a local userId to avoid race with state updates
        let userId = currentUserId;
        if (!userId) {
          const { data } = await supabase.auth.getUser();
          userId = data.user?.id ?? null;
          setCurrentUserId(userId);
        }
        setIsUserHighest(!!(highestBid && userId && highestBid.bidder_user_id === userId));
       } catch (e) {
         console.error('Failed to fetch latest highest bid', e);
       } finally {
         if (isMounted) setRefreshingLatest(false);
       }
     };
     refreshLatest();
     return () => { isMounted = false; };
   }, [open, product.auction?.id]);

   // Initialize bid amount to minimum required bid and clear errors
  useEffect(() => {
    if (auction) {
      const minBid = currentBid + bidIncrement;
      setBidAmount(minBid);
      setError(''); // Clear any existing errors when popup opens
    }
  }, [auction, currentBid, bidIncrement]);

  // Clear error when bid amount changes
  useEffect(() => {
    if (bidAmount > 0) {
      setError('');
    }
  }, [bidAmount]);

  // Countdown timer
  useEffect(() => {
    if (!auction?.end_time) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const endTime = new Date(auction.end_time);
      const difference = endTime.getTime() - now.getTime();

      if (difference <= 0) {
        setTimeLeft('Auction Ended');
        setIsEnded(true);
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      setIsEnded(false);
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [auction?.end_time]);

  // Realtime: listen for new/updated bids on this auction and refresh highest bid
  useEffect(() => {
    if (!open || !product.auction?.id) return;

    const channel = supabase
      .channel(`auction-bids-${product.auction.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'auction_bids',
          filter: `auction_id=eq.${product.auction.id}`,
        },
        async () => {
          try {
            const bids = await AuctionService.getAuctionBids(product.auction!.id);
            const highestBid = bids && bids.length > 0 ? bids[0] : null;
            const highestAmount = highestBid ? highestBid.bid_amount : (product.auction!.current_bid ?? product.price);

            setLatestAuction(prev => ({
              id: product.auction!.id,
              end_time: (prev?.end_time ?? product.auction!.end_time),
              current_bid: highestAmount,
              bid_increment: product.auction!.bid_increment ?? 1.0,
              status: product.auction!.status,
              reserve_price: product.auction!.reserve_price,
              bid_count: bids?.length ?? (prev?.bid_count || 0),
            }));

            // Ensure we have the user id (read locally to avoid setState race)
            let userId = currentUserId;
            if (!userId) {
              const { data } = await supabase.auth.getUser();
              userId = data.user?.id ?? null;
              setCurrentUserId(userId);
            }

            const userIsHighest = !!(highestBid && userId && highestBid.bidder_user_id === userId);
            setIsUserHighest(userIsHighest);

            // If outbid, notify and bump the suggested amount
            if (!userIsHighest) {
              setOutbidHighest(highestAmount);
              setOutbidOpen(true);
              const nextMin = highestAmount + bidIncrement;
              setBidAmount(parseFloat(nextMin.toFixed(2)));
            }
          } catch (e) {
            // ignore realtime refresh errors
          }
        }
      )
      .subscribe();

    return () => {
      try { supabase.removeChannel(channel); } catch {}
    };
  }, [open, product.auction?.id, currentUserId, bidIncrement]);

  const handleBidAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = event.target.value.replace(',', '.');
    const value = parseFloat(normalized) || 0;
    setBidAmount(value);
    setError(''); // Clear error when user types
  };

  const handleQuickBid = (amount: number) => {
    setBidAmount(amount);
    setError('');
  };

  const handleClose = () => {
    setError('');
    setBidSuccess(false);
    setOutbidOpen(false);
    setEndedOpen(false);
    onClose();
  };

  const validateBid = (amount: number): string | null => {
    if (!amount || amount <= 0) {
      return 'Please enter a valid bid amount';
    }
    if (amount <= currentBid) {
      return `Bid must be higher than current bid of $${currentBid.toFixed(2)}`;
    }
    if (amount < currentBid + bidIncrement) {
      return `Minimum bid is $${(currentBid + bidIncrement).toFixed(2)}`;
    }
    if (reservePrice > 0 && amount < reservePrice) {
      return `Bid is below reserve price of $${reservePrice.toFixed(2)}`;
    }
    return null;
  };

  const handlePlaceBid = async () => {
    // Prevent bidding if ended
    if (isEnded || auction?.status === 'ended') {
      setEndedOpen(true);
      setError('Auction has ended. No more bids can be placed.');
      return;
    }
    const validationError = validateBid(bidAmount);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsPlacingBid(true);
    setError('');

    let nonFatalUpdateError = false;
    try {
      try {
        await onPlaceBid(bidAmount);
      } catch (err: any) {
        const msg = typeof err?.message === 'string' ? err.message : '';
        // Treat PostgREST coercion errors as non-fatal (happen on updateAuction select())
        if (msg.includes('Cannot coerce the result to a single JSON object') || msg.startsWith('Failed to update auction')) {
          nonFatalUpdateError = true;
        } else {
          setError(err instanceof Error ? err.message : 'Failed to place bid');
          return;
        }
      }

      // Immediately refresh highest bid after placing (even if a non-fatal update error occurred)
      if (product.auction?.id) {
        try {
          const bids = await AuctionService.getAuctionBids(product.auction.id);
          const highest = bids && bids.length > 0 ? bids[0].bid_amount : bidAmount;
          setLatestAuction(prev => ({
            id: product.auction!.id,
            end_time: (prev?.end_time ?? product.auction!.end_time),
            current_bid: highest,
            bid_increment: product.auction!.bid_increment ?? 1.0,
            status: product.auction!.status,
            reserve_price: product.auction!.reserve_price,
            bid_count: bids?.length ?? (prev?.bid_count || 0),
          }));

          if (highest > bidAmount) {
            // Outbid immediately
            setOutbidHighest(highest);
            setOutbidOpen(true);
            const nextMin = highest + bidIncrement;
            setBidAmount(parseFloat(nextMin.toFixed(2)));
            setBidSuccess(false);
          } else {
            setBidSuccess(true);
            // Stay open; reflect that user is currently highest
            setIsUserHighest(true);
          }
        } catch (_) {
          // If refresh fails, still show success and update UI optimistically
          setLatestAuction(prev => ({
            id: product.auction!.id,
            end_time: (prev?.end_time ?? product.auction!.end_time),
            current_bid: Math.max(bidAmount, prev?.current_bid ?? product.price),
            bid_increment: product.auction!.bid_increment ?? 1.0,
            status: product.auction!.status,
            reserve_price: product.auction!.reserve_price,
            bid_count: (prev?.bid_count || 0) + 1,
          }));
          setBidSuccess(true);
        }
      } else {
        setBidSuccess(true);
      }
      // Clear any previous non-fatal error message
      if (nonFatalUpdateError) setError('');
    } finally {
      setIsPlacingBid(false);
    }
  };

  const baseForSuggestions = outbidHighest != null ? outbidHighest + bidIncrement : (currentBid + bidIncrement);
  const quickBidAmounts = [
    baseForSuggestions,
    baseForSuggestions + bidIncrement,
    baseForSuggestions + (bidIncrement * 2),
    Math.ceil((outbidHighest ?? currentBid) * 1.1),
    Math.ceil((outbidHighest ?? currentBid) * 1.25)
  ].map(v => parseFloat(Number(v).toFixed(2)));

  return (
    <>
       <Dialog
         open={open}
         onClose={handleClose}
         maxWidth="md"
         fullWidth
         scroll="paper"
         PaperProps={{
           sx: {
             borderRadius: { xs: 0, sm: 3 },
             boxShadow: { xs: 'none', sm: '0 20px 40px rgba(0,0,0,0.15)' },
             overflow: 'hidden',
             maxHeight: { xs: '100vh', sm: '90vh' },
             display: 'flex',
             flexDirection: 'column',
             m: { xs: 0, sm: 2 },
             width: { xs: '100vw', sm: 'auto' },
             height: { xs: '100vh', sm: 'auto' }
           }
         }}
       >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
           {/* Header */}
           <DialogTitle sx={{ 
             bgcolor: '#ff9800', 
             color: 'white', 
             p: { xs: 2, sm: 3 },
             position: 'relative',
             overflow: 'hidden',
             flexShrink: 0
           }}>
             <Box sx={{ 
               display: 'flex', 
               alignItems: 'center', 
               gap: { xs: 1.5, sm: 2 },
               flexDirection: { xs: 'column', sm: 'row' },
               textAlign: { xs: 'center', sm: 'left' }
             }}>
               <Avatar sx={{ 
                 bgcolor: 'rgba(255,255,255,0.2)',
                 width: { xs: 40, sm: 48 },
                 height: { xs: 40, sm: 48 }
               }}>
                 <Gavel />
               </Avatar>
               <Box>
                 <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                   Place Your Bid
                 </Typography>
                 <Typography variant="body2" sx={{ 
                   opacity: 0.9,
                   fontSize: { xs: '0.8rem', sm: '0.875rem' },
                   wordBreak: 'break-word'
                 }}>
                   {product.name}
                 </Typography>
               </Box>
             </Box>
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                zIndex: 5
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

           <DialogContent sx={{ 
             p: 0, 
             flex: 1,
             display: 'flex',
             flexDirection: 'column',
             overflow: 'hidden'
           }}>
             <Box sx={{ 
               overflow: 'auto', 
               flex: 1,
               display: 'flex',
               flexDirection: 'column',
               maxHeight: '100%',
               minHeight: 0
             }}>
               {/* Product Info */}
               <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8f9fa' }}>
               <Box sx={{ 
                 display: 'flex', 
                 gap: { xs: 2, sm: 3 }, 
                 alignItems: 'center',
                 flexDirection: { xs: 'column', sm: 'row' }
               }}>
                 <Box
                   component="img"
                   src={product.image}
                   alt={product.name}
                   sx={{
                     width: { xs: 100, sm: 120 },
                     height: { xs: 100, sm: 120 },
                     objectFit: 'cover',
                     borderRadius: 2,
                     border: '3px solid #ff9800',
                     flexShrink: 0
                   }}
                 />
                 <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {product.name}
                  </Typography>
                  
                   {/* Auction Status */}
                   <Box sx={{ 
                     display: 'flex', 
                     gap: 1, 
                     mb: 2, 
                     flexWrap: 'wrap',
                     justifyContent: { xs: 'center', sm: 'flex-start' }
                   }}>
                    <Chip
                      label="AUCTION"
                      icon={<Gavel />}
                      color="warning"
                      size="small"
                    />
                     {isUserHighest ? (
                       <Chip
                         label="You are highest"
                         color="success"
                         size="small"
                         icon={<CheckCircle />}
                         sx={{ fontWeight: 'bold' }}
                       />
                     ) : (
                       <Chip
                         label="Outbid"
                         color="error"
                         size="small"
                         icon={<Warning />}
                         sx={{ fontWeight: 'bold' }}
                       />
                     )}
                    {isEnded || auction?.status === 'ended' ? (
                      <Chip
                        label="Auction Ended"
                        color="error"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label={timeLeft}
                        icon={<AccessAlarm />}
                        color="error"
                        size="small"
                      />
                    )}
                    <Chip
                      label={`${bidCount} bids`}
                      icon={<TrendingUp />}
                      color="info"
                      size="small"
                    />
                  </Box>

                  {/* Current Bid Info */}
                  <Card sx={{ bgcolor: 'white', boxShadow: 2, cursor: 'pointer' }} onClick={() => setHistoryOpen(true)}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Current Bid
                          </Typography>
                           <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                             {refreshingLatest ? (
                               <LinearProgress sx={{ width: 160 }} />
                             ) : (
                               <Typography variant="h4" fontWeight="bold" color="#d32f2f">
                                 ${currentBid.toFixed(2)}
                               </Typography>
                             )}
                           </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" color="text.secondary">
                            Bid Increment
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="#ff9800">
                            ${bidIncrement.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                      {reservePrice > 0 && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
                          <Typography variant="body2" color="text.secondary">
                            Reserve Price: <strong>${reservePrice.toFixed(2)}</strong>
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>

             {/* Bid Input Section */}
             <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Enter Your Bid Amount
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Bid Amount"
                  value={bidAmount > 0 ? bidAmount.toString() : ''}
                  onChange={handleBidAmountChange}
                  error={!!error}
                  helperText={error || `Minimum bid: $${(currentBid + bidIncrement).toFixed(2)}`}
                  InputProps={{
                    startAdornment: <AttachMoney sx={{ mr: 1, color: '#ff9800' }} />,
                    sx: { fontSize: '1.2rem' }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.2rem',
                      fontWeight: 'bold'
                    }
                  }}
                />
              </Box>

               {/* Quick Bid Buttons */}
               <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                 Quick Bid Options:
               </Typography>
               <Box sx={{ 
                 display: 'flex', 
                 gap: 1, 
                 mb: 3, 
                 flexWrap: 'wrap',
                 justifyContent: { xs: 'center', sm: 'flex-start' }
               }}>
                {quickBidAmounts.map((amount, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    size="small"
                    onClick={() => handleQuickBid(amount)}
                    sx={{
                      borderColor: '#ff9800',
                      color: '#ff9800',
                      '&:hover': {
                        borderColor: '#f57c00',
                        bgcolor: '#fff3e0'
                      }
                    }}
                  >
                    ${amount.toFixed(2)}
                  </Button>
                ))}
              </Box>

               {/* Bid Summary */}
               <Card sx={{ bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
                 <CardContent sx={{ p: 2 }}>
                   <Typography variant="subtitle2" color="#e65100" gutterBottom>
                     Your Bid Summary:
                   </Typography>
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Typography variant="h6" fontWeight="bold" color="#d32f2f">
                       ${bidAmount.toFixed(2)}
                     </Typography>
                     {bidAmount > currentBid && (
                       <Chip
                         label="Winning Bid"
                         icon={<CheckCircle />}
                         color="success"
                         size="small"
                       />
                     )}
                   </Box>
                   {bidAmount >= (reservePrice || 0) && reservePrice > 0 && (
                     <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                       ✓ Meets reserve price requirement
                     </Typography>
                   )}
                 </CardContent>
               </Card>

             </Box>
             </Box>
           </DialogContent>

          {/* Ended Overlay (scoped to dialog only) */}
          {(isEnded || auction?.status === 'ended') && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 3
              }}
            >
              <Card sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, textAlign: 'center', maxWidth: 460, mx: 2 }} elevation={8}>
                <Avatar sx={{ bgcolor: 'error.main', width: 72, height: 72, mx: 'auto', mb: 2 }}>
                  <Timer />
                </Avatar>
                <Typography variant="h5" fontWeight="bold" color="error.main" gutterBottom>
                  Auction Ended
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This auction has concluded. Bidding is closed.
                </Typography>
                <Chip label="No more bids" color="error" icon={<Gavel />} sx={{ fontWeight: 'bold' }} />
              </Card>
            </Box>
          )}

           {/* Footer */}
           <DialogActions sx={{ 
             p: { xs: 2, sm: 3 }, 
             bgcolor: '#f8f9fa', 
             gap: 2, 
             flexShrink: 0,
             flexDirection: { xs: 'column', sm: 'row' }
           }}>
             <Button
               onClick={onClose}
               variant="outlined"
               size="large"
               sx={{ 
                 minWidth: { xs: '100%', sm: 120 },
                 width: { xs: '100%', sm: 'auto' }
               }}
             >
               Cancel
             </Button>
             <Button
               onClick={handlePlaceBid}
               variant="contained"
               size="large"
              disabled={isPlacingBid || !!error || bidAmount <= currentBid || isEnded || auction?.status === 'ended'}
               startIcon={!isPlacingBid ? <Gavel /> : undefined}
               sx={{
                 minWidth: { xs: '100%', sm: 160 },
                 width: { xs: '100%', sm: 'auto' },
                 bgcolor: '#ff9800',
                 '&:hover': { bgcolor: '#f57c00' },
                 '&:disabled': { bgcolor: '#ccc' }
               }}
             >
               {isPlacingBid ? (
                 <CircularProgress size={22} sx={{ color: 'white' }} />
               ) : (
                 'Place Bid'
               )}
             </Button>
           </DialogActions>
        </motion.div>
      </Dialog>

      {/* Bid History Popup */}
      {product.auction?.id && (
        <BidPlacementHistoryPopup
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          auctionId={product.auction.id}
          productName={product.name}
        />
      )}


      {/* Success Snackbar */}
      <Snackbar
        open={bidSuccess}
        autoHideDuration={2000}
        onClose={() => setBidSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setBidSuccess(false)}
          severity="success"
          icon={<CheckCircle />}
          sx={{ width: '100%' }}
        >
          Bid placed successfully! You are now the highest bidder.
        </Alert>
      </Snackbar>

      {/* Outbid Snackbar */}
      <Snackbar
        open={outbidOpen}
        autoHideDuration={3500}
        onClose={() => setOutbidOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOutbidOpen(false)}
          severity="warning"
          icon={<Warning />}
          sx={{ width: '100%' }}
        >
          You were outbid. Highest bid is now ${outbidHighest?.toFixed(2)}. Try a higher bid.
        </Alert>
      </Snackbar>

      {/* Ended Snackbar */}
      <Snackbar
        open={endedOpen}
        autoHideDuration={3000}
        onClose={() => setEndedOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setEndedOpen(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          Auction has ended. No more bids can be placed.
        </Alert>
      </Snackbar>
    </>
  );
};

export default BidPlacementPopup;
